import React from 'react';

import {
  Container,
  Accordion,
  AccordionPanel,
  AccordionItem,
  AccordionIconButton,
  AccordionHeader,
} from '@tq/ui-components';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { tqc } from '@tq/ui-components/dist/system';

import { PageHeading } from 'components';
import { OrganisationDetailsPanel, TeamsPanel, UsersPanel } from './components';
import { organisationCreatedState } from 'store';
import { useLocation } from 'react-router-dom';
import { Organisation } from 'types';

export type ManageOrganisationProps = {
  organisation?: Organisation;
};

export const ManageOrganisation: React.FC<ManageOrganisationProps> = ({ organisation }) => {
  /**
   * Returns the main container UI for creating an Organisation.
   *
   * @remarks
   * TQ admins only
   */
  const { t } = useTranslation('manage_organisation');
  const location = useLocation();
  const newOrganisation = useRecoilValue(organisationCreatedState);

  const getDefaultIndex = (): [number] => {
    let defaultIndex = 0;
    if (location.pathname.endsWith('teams')) {
      defaultIndex = 1;
    }
    if (location.pathname.endsWith('users')) {
      defaultIndex = 2;
    }
    return [defaultIndex];
  };

  return (
    <Container px={{ base: 0, lg: 4, xl: 0 }}>
      <PageHeading
        heading={
          organisation?.organisationName
            ? `${t('manage', 'Manage')} ${organisation.organisationName}`
            : t('new_page_heading', 'TQ Admin - Create new organisation')
        }
      />
      <Accordion allowMultiple defaultIndex={getDefaultIndex()}>
        <AccordionItem my={8}>
          <h2>
            <AccordionHeader>
              <tqc.div flex='1' textAlign='left'>
                {t('organisation_details', 'Organisation details')}
              </tqc.div>
              <AccordionIconButton />
            </AccordionHeader>
          </h2>
          <AccordionPanel>
            <OrganisationDetailsPanel organisation={organisation} />
          </AccordionPanel>
        </AccordionItem>
        <AccordionItem my={8}>
          <h2>
            <AccordionHeader>
              <tqc.div flex='1' textAlign='left'>
                {t('teams', 'Teams')}
              </tqc.div>
              <AccordionIconButton />
            </AccordionHeader>
          </h2>
          <AccordionPanel>
            <TeamsPanel organisation={organisation ?? newOrganisation} />
          </AccordionPanel>
        </AccordionItem>
        <AccordionItem my={8}>
          <h2>
            <AccordionHeader>
              <tqc.div flex='1' textAlign='left'>
                {t('users', 'Users')}
              </tqc.div>
              <AccordionIconButton />
            </AccordionHeader>
          </h2>
          <AccordionPanel>
            <UsersPanel organisation={organisation ?? newOrganisation} />
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Container>
  );
};
